import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { warrantyApi } from '../../services/api';

interface RmaRequest {
  id: string;
  rmaNumber: string;
  orderNumber?: string;
  component: string;
  issueType: string;
  description: string;
  status: string;
  adminNotes?: string;
  denialReason?: string;
  photoUrls: string[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

const statusColors: Record<string, string> = {
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
  replacement_shipped: 'bg-blue-100 text-blue-800',
  resolved: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  denied: 'Denied',
  replacement_shipped: 'Replacement Shipped',
  resolved: 'Resolved',
};

const componentLabels: Record<string, string> = {
  motor: 'Motor',
  fabric: 'Fabric',
  track: 'Track',
  housing: 'Housing',
  remote: 'Remote',
  other: 'Other',
};

const issueLabels: Record<string, string> = {
  manufacturing_defect: 'Manufacturing Defect',
  shipping_damage: 'Shipping Damage',
  motor_failure: 'Motor Failure',
  fabric_tear: 'Fabric Tear',
  track_binding: 'Track Binding',
  weather_damage: 'Weather Damage',
  other: 'Other',
};

export const MyRmasPage: React.FC = () => {
  const [rmas, setRmas] = useState<RmaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selected RMA for detail view
  const [selectedRma, setSelectedRma] = useState<RmaRequest | null>(null);

  useEffect(() => {
    fetchRmas();
  }, [page, searchQuery, statusFilter]);

  const fetchRmas = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = { page, pageSize: 20 };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await warrantyApi.listRmas(params);
      setRmas(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load RMAs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My RMA Requests</h1>
          <p className="text-gray-400">Track your warranty claim requests</p>
        </div>
        <Link to="/warranty/submit-rma" className="btn-primary">
          + Submit RMA
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by RMA number, order number..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="input-field w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="select-field"
          >
            <option value="all">All Statuses</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="replacement_shipped">Replacement Shipped</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {rmas.length} of {total} RMA requests
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading RMAs...</p>
        </div>
      ) : rmas.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-gray-400 text-5xl mb-4">&#128221;</div>
          <h3 className="text-lg font-medium text-white">No RMA requests found</h3>
          <p className="text-gray-400 mt-1">
            {searchQuery || statusFilter !== 'all'
              ? 'No RMAs match your search criteria.'
              : "You haven't submitted any RMA requests yet."}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link to="/warranty/submit-rma" className="inline-block mt-4 btn-primary">
              Submit Your First RMA
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* RMAs Table */}
          <div className="card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-keder-gray">
                <thead className="bg-keder-gray/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      RMA #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Component
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Issue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-keder-gray/50">
                  {rmas.map((rma) => (
                    <tr key={rma.id} className="hover:bg-keder-gray/20">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-keder-yellow font-medium">{rma.rmaNumber}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-white">
                        {rma.orderNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                        {componentLabels[rma.component] || rma.component}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                        {issueLabels[rma.issueType] || rma.issueType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[rma.status]}`}
                        >
                          {statusLabels[rma.status] || rma.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-sm">
                        {formatDate(rma.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedRma(rma)}
                          className="text-keder-yellow hover:text-yellow-400"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-keder-gray/30 px-6 py-4 flex items-center justify-between border-t border-keder-gray">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedRma && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">RMA Details</h2>
                  <p className="text-keder-yellow font-medium">{selectedRma.rmaNumber}</p>
                </div>
                <button
                  onClick={() => setSelectedRma(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Status Banner */}
                <div
                  className={`p-4 rounded-lg ${
                    selectedRma.status === 'approved'
                      ? 'bg-green-900/30 border border-green-600'
                      : selectedRma.status === 'denied'
                      ? 'bg-red-900/30 border border-red-600'
                      : selectedRma.status === 'pending_review'
                      ? 'bg-yellow-900/30 border border-yellow-600'
                      : 'bg-blue-900/30 border border-blue-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 text-sm font-medium rounded-full ${statusColors[selectedRma.status]}`}
                    >
                      {statusLabels[selectedRma.status]}
                    </span>
                    {selectedRma.resolvedAt && (
                      <span className="text-gray-400 text-sm">
                        Resolved on {formatDate(selectedRma.resolvedAt)}
                      </span>
                    )}
                  </div>
                  {selectedRma.denialReason && (
                    <p className="text-red-300 mt-2">
                      <strong>Reason:</strong> {selectedRma.denialReason}
                    </p>
                  )}
                  {selectedRma.adminNotes && (
                    <p className="text-gray-300 mt-2">
                      <strong>Notes:</strong> {selectedRma.adminNotes}
                    </p>
                  )}
                </div>

                {/* Request Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Request Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-400 text-sm">Order Number:</span>
                      <p className="text-white">{selectedRma.orderNumber || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Submitted:</span>
                      <p className="text-white">{formatDate(selectedRma.createdAt)}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Component:</span>
                      <p className="text-white">{componentLabels[selectedRma.component]}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Issue Type:</span>
                      <p className="text-white">{issueLabels[selectedRma.issueType]}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Description</h3>
                  <p className="text-white bg-keder-gray/30 p-3 rounded-lg">
                    {selectedRma.description}
                  </p>
                </div>

                {/* Photos */}
                {selectedRma.photoUrls && selectedRma.photoUrls.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Photos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {selectedRma.photoUrls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={url}
                            alt={`Issue ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={() => setSelectedRma(null)} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRmasPage;
